// GERADO de backend-senne/src/contracts — NÃO editar; rode scripts/sync-contracts.sh

import { z } from 'zod'

/**
 * The retirada compulsória on the core (spec 20261026-142200, AC-14): what `/api/compulsory` answers and accepts. The
 * statuses keep the legacy numbers and labels, registered vocabulary. The blob's name and address never appear here: a
 * document is named by its `file_id` under the retirada's public id.
 */

export const COMPULSORY_WITHDRAWAL_STATUS_LABELS = {
    0: 'Solicitada',
    1: 'Amostra Separada',
    2: 'Amostra Retirada',
    3: 'Sem Amostra',
} as const

export const compulsoryWithdrawalStatusCodeSchema = z.union([
    z.literal(0),
    z.literal(1),
    z.literal(2),
    z.literal(3),
])
export type CompulsoryWithdrawalStatusCode = z.infer<
    typeof compulsoryWithdrawalStatusCodeSchema
>

/** One retirada as the screens show it. `.strict()`, so a field that starts leaking fails here. */
export const compulsoryWithdrawalSchema = z
    .object({
        source: z.literal('core'),
        type: z.literal('withdrawal'),
        /** The retirada's number. */
        id: z.number().int().positive(),
        /** Its public id: what the change and the files name. */
        withdrawal_uuid: z.string().uuid(),
        /** The attendance's number, the one the screens show. */
        appointment_id: z.number().int().positive(),
        hospital_name: z.string(),
        hospital_uuid: z.string().nullable(),
        patient_name: z.string(),
        doctor_name: z.string().nullable(),
        status: compulsoryWithdrawalStatusCodeSchema,
        desc_status: z.string(),
        sinan: z.string().nullable(),
        /** Its first document, as the legacy row carried one. */
        file_id: z.string().nullable(),
        withdrawal_name: z.string().nullable(),
        withdrawal_rg: z.string().nullable(),
        created_at: z.string(),
        updated_at: z.string(),
    })
    .strict()
export type CompulsoryWithdrawal = z.infer<typeof compulsoryWithdrawalSchema>

export const compulsoryWithdrawalListItemSchema = compulsoryWithdrawalSchema
    .extend({ total_rows: z.number().int().nonnegative() })
    .strict()
export type CompulsoryWithdrawalListItem = z.infer<
    typeof compulsoryWithdrawalListItemSchema
>

/** `GET /api/compulsory` and `/filter`, inside the `data` envelope. */
export const compulsoryWithdrawalListSchema = z.object({
    items: z.array(compulsoryWithdrawalListItemSchema),
    total: z.number().int().nonnegative(),
    count: z.number().int().nonnegative(),
    hasMore: z.boolean(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    page_size: z.number().int().positive(),
    links: z.array(z.never()),
})
export type CompulsoryWithdrawalList = z.infer<
    typeof compulsoryWithdrawalListSchema
>

/** `GET /api/compulsory/counter`, inside the `data` envelope: the legacy shape, one row. */
export const compulsoryWithdrawalCounterSchema = z.object({
    items: z
        .array(
            z.object({
                qt_solicitada: z.number().int().nonnegative(),
                qt_separada: z.number().int().nonnegative(),
                qt_retirada: z.number().int().nonnegative(),
                qt_sem_amostra: z.number().int().nonnegative(),
            })
        )
        .length(1),
    count: z.literal(1),
    hasMore: z.literal(false),
    limit: z.literal(1),
    offset: z.literal(0),
    links: z.array(z.never()),
})
export type CompulsoryWithdrawalCounter = z.infer<
    typeof compulsoryWithdrawalCounterSchema
>

/** Whether an attendance can take a retirada now. */
export const COMPULSORY_WITHDRAWAL_ELIGIBILITY = [
    'allowed',
    'no_addition',
    'already_requested',
    'attendance_cancelled',
] as const
export const compulsoryWithdrawalEligibilitySchema = z.enum(
    COMPULSORY_WITHDRAWAL_ELIGIBILITY
)
export type CompulsoryWithdrawalEligibility = z.infer<
    typeof compulsoryWithdrawalEligibilitySchema
>

/** `GET /api/compulsory/:attendanceUuid`, inside the `data` envelope. */
export const compulsoryWithdrawalOfAttendanceSchema = z.object({
    appointment_id: z.number().int().positive(),
    eligibility: compulsoryWithdrawalEligibilitySchema,
    withdrawal: compulsoryWithdrawalSchema.nullable(),
})
export type CompulsoryWithdrawalOfAttendance = z.infer<
    typeof compulsoryWithdrawalOfAttendanceSchema
>

/** The text fields of `POST /api/compulsory` (multipart; the file goes in `document`). */
export const createCompulsoryWithdrawalFieldsSchema = z.object({
    attendance_id: z.string().uuid('Atendimento inválido.'),
    sinan: z
        .string()
        .trim()
        .max(50, 'Número do SINAN deve ter no máximo 50 caracteres')
        .optional()
        .transform((value) => value || undefined),
})
export type CreateCompulsoryWithdrawalFields = z.infer<
    typeof createCompulsoryWithdrawalFieldsSchema
>

/** `POST /api/compulsory` answers 201 with this, inside the `data` envelope. */
export const createdCompulsoryWithdrawalSchema = z.object({
    withdrawal_uuid: z.string().uuid(),
    appointment_id: z.number().int().positive(),
    status: z.literal(0),
})
export type CreatedCompulsoryWithdrawal = z.infer<
    typeof createdCompulsoryWithdrawalSchema
>

/** `PUT /api/compulsory/:uuid`. The legacy messages, with the bounds the core stores. */
export const changeCompulsoryWithdrawalSchema = z
    .object({
        status: z
            .number({
                required_error: 'Informe o novo status da retirada.',
                invalid_type_error: 'Status deve ser 0, 1, 2 ou 3',
            })
            .int('Status deve ser inteiro')
            .min(0, 'Status deve ser 0, 1, 2 ou 3')
            .max(3, 'Status deve ser 0, 1, 2 ou 3'),
        withdrawal_name: z
            .string()
            .trim()
            .min(3, 'Nome deve ter no mínimo 3 caracteres')
            .max(120, 'Nome deve ter no máximo 120 caracteres')
            .optional(),
        withdrawal_rg: z
            .string()
            .trim()
            .min(5, 'RG deve ter no mínimo 5 caracteres')
            .max(20, 'RG deve ter no máximo 20 caracteres')
            .optional(),
    })
    .refine(
        (data) =>
            data.status !== 2 ||
            (Boolean(data.withdrawal_name) && Boolean(data.withdrawal_rg)),
        {
            message: 'Nome e RG são obrigatórios para status concluído (2)',
            path: ['withdrawal_name'],
        }
    )
export type ChangeCompulsoryWithdrawal = z.infer<
    typeof changeCompulsoryWithdrawalSchema
>

/** `POST /api/compulsory/attendances/search`: the patient step. The name and the date are never stored. */
export const searchCompulsoryAttendancesSchema = z.object({
    name: z.string().max(255),
    birthDate: z.string().max(32),
    /** The active hospital (its portal uuid). A doctor's search ignores it: it runs on their own attendances. */
    hospital: z.string().max(64).optional(),
})
export type SearchCompulsoryAttendances = z.infer<
    typeof searchCompulsoryAttendancesSchema
>

export const compulsoryAttendanceHitSchema = z.object({
    /** The attendance's number, the one the screen shows. */
    appointment_id: z.number().int().positive(),
    /** What the create sends back. */
    attendance_uuid: z.string().uuid(),
    entered_at: z.string(),
    convenio: z.string().nullable(),
    eligibility: compulsoryWithdrawalEligibilitySchema,
})
export type CompulsoryAttendanceHit = z.infer<
    typeof compulsoryAttendanceHitSchema
>

export const compulsoryAttendanceSearchResultSchema = z.object({
    patients: z.array(
        z.object({
            name: z.string(),
            birthDate: z.string().nullable(),
            attendances: z.array(compulsoryAttendanceHitSchema),
        })
    ),
})
export type CompulsoryAttendanceSearchResult = z.infer<
    typeof compulsoryAttendanceSearchResultSchema
>

/** `GET /api/compulsory/files/:uuid`: a document with a neutral name, never the stored one. */
export const compulsoryWithdrawalDocumentSchema = z
    .object({
        file_id: z.string(),
        name: z.string(),
        size: z.number().int().nonnegative(),
        created_at: z.string(),
    })
    .strict()
export type CompulsoryWithdrawalDocument = z.infer<
    typeof compulsoryWithdrawalDocumentSchema
>
